import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  pagePropType,
  searchPropType,
  sortPropType,
  StudentTableInit,
  StudentTableType
} from "redux/initialization/StudentTableInit";
import api from "ApiClient";
import { ApiURL, getEnabled } from "utils/Actions";
import {
  ADM_APPLICATION_STATUS,
  API_RESPONSE_STATUS,
  TABLE_TYPE,
  DATE_FORMAT,
  FILTER_LABEL
} from "utils/Constants";
import { common } from "utils/Wording";
import { showErrorModal } from "utils/hooks/handleNotification";
import {
  generateTableData,
  createPaginatedTableData,
  getStatusAndStyling,
  applySort
} from "utils/hooks/studentTableActions";
import { defaultSorting } from "utils/AssetsData";
import moment from "moment";
import { ApiUgOsQueryParams } from "utils/Types";
import { RootState } from "redux/store";
import { buildSourceQueryParams } from "utils/Actions";
import { WHITELABEL } from "utils/Constants";

// optional source param = 'ug' | 'os' | null
// https://uac-cloud.atlassian.net/browse/ABKL-82
export const fetchStudentTable = createAsyncThunk(
  "ref/students",
  async ({ source = null }: ApiUgOsQueryParams, thunkAPI) => {
    const queryParams = buildSourceQueryParams(thunkAPI.getState() as RootState, source);
    try {
      const res = await api.get(ApiURL.students, queryParams);
      if (res?.data?.status === API_RESPONSE_STATUS.ERROR) {
        throw new Error(common.loadingError);
      }
      return res?.data?.data ? res.data.data : [];
    } catch (error: any) {
      console.error(error);
      showErrorModal({
        title: common.failAsync,
        errors: error.response?.data?.errors ? error.response.data.errors : [error.message]
      });
      return StudentTableInit.data;
    }
  },
  {
    condition: ({ source = null }, { getState }) => {
      const whiteLabel = (getState() as RootState).properties.whiteLabel as WHITELABEL | null;
      return getEnabled(source, whiteLabel);
    }
  }
);

function getProperty<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}

export const StudentTableSlice = createSlice({
  name: "student-table",
  initialState: StudentTableInit,
  reducers: {
    // Search
    searchTable: (state, action: PayloadAction<searchPropType>) => {
      let newPaginatedData;
      const { target, role, targetOrigin } = action.payload;

      const prop = getProperty(state, role);
      prop.loading = true;

      const { origin, filters, sorts } = prop;
      const newTableData = generateTableData(origin, target, filters, sorts);

      if (newTableData.length > 0) {
        newPaginatedData = createPaginatedTableData(1, newTableData);
      } else {
        newPaginatedData = [];
      }

      if (role === TABLE_TYPE.SRS) {
        prop.searchOrigin = targetOrigin;
      }

      prop.loading = false;
      prop.currentPage = 1;
      prop.tableData = newTableData;
      prop.paginatedTableData = newPaginatedData;
      prop.search = target;
    },
    // Page navigation
    updateCurrentPage: (state, action: PayloadAction<pagePropType>) => {
      const { currentPage, role } = action.payload;
      const prop = getProperty(state, role);
      prop.loading = true;

      const { tableData } = prop;
      const newPaginatedData = createPaginatedTableData(currentPage, tableData);

      prop.loading = false;
      prop.currentPage = currentPage;
      prop.paginatedTableData = newPaginatedData;
    },
    // Sorting
    sortTable: (state, action: PayloadAction<sortPropType>) => {
      const { sortObj, role } = action.payload;
      const prop = getProperty(state, role);
      prop.loading = true;

      const { tableData, currentPage, filters, search } = prop;
      const newTableData = generateTableData(tableData, search, filters, sortObj);
      const newPaginatedData = createPaginatedTableData(currentPage, newTableData);

      prop.loading = false;
      prop.tableData = newTableData;
      prop.paginatedTableData = newPaginatedData;
      prop.sorts = sortObj;
    },
    // Filter
    filterTable: (state, action) => {
      const { newFilters, role } = action.payload;
      const prop = getProperty(state, role);
      prop.loading = true;

      const { origin, sorts, search } = prop;
      const newTableData = generateTableData(origin, search, newFilters, sorts);
      const newPaginatedData = createPaginatedTableData(1, newTableData);

      prop.loading = false;
      prop.currentPage = 1;
      prop.tableData = newTableData;
      prop.paginatedTableData = newPaginatedData;
      prop.filters = newFilters;
    },
    applyCriteria: (state, action) => {
      const { role, currentPage, filters, sorts, search } = action.payload;
      if (role != TABLE_TYPE.SRS) {
        return;
      }
      state[TABLE_TYPE.SRS].loading = true;

      const prop = getProperty(state, role);
      const { origin } = prop;
      const newTableData = generateTableData(origin, search, filters, sorts);
      const newPaginatedData = createPaginatedTableData(currentPage, newTableData);

      prop.loading = false;
      prop.currentPage = currentPage;
      prop.sorts = sorts;
      prop.filters = filters;
      prop.search = search;
      prop.tableData = newTableData;
      prop.paginatedTableData = newPaginatedData;
      state[TABLE_TYPE.SRS].loading = false;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchStudentTable.pending, () => {
      return {
        ...StudentTableInit,
        isLoading: true
      };
    });
    builder.addCase(
      fetchStudentTable.fulfilled,
      (state, action: PayloadAction<StudentTableType[]>) => {
        const students = action.payload;

        if (students) {
          const totalApplicants = students.length;
          let totalAppliedWhitelabel = 0;
          const tableDataForAdmin: Array<any> = [],
            tableDataForCSV: Array<any> = [],
            tableDataForRater: Array<any> = [];

          students.forEach((student: any) => {
            const isApplicationLodged =
              student?.admissionApplicationStatus === ADM_APPLICATION_STATUS.LODGED;
            const ratingStatus = getStatusAndStyling(student.schoolRated).label;

            // Numbers in dashboard
            if (isApplicationLodged) {
              totalAppliedWhitelabel += 1;
            }

            // ADMIN table
            tableDataForAdmin.push({
              key: student.studentNumber,
              givenName1: student.givenName1,
              familyName: student.familyName,
              studentNumber: student.studentNumber,
              dob: moment(student.dob, DATE_FORMAT.Y_M_D).format(DATE_FORMAT.D_M_Y),
              appliedAdmission: isApplicationLodged,
              srsLodged: student.srsLodged,
              schoolRated: student.schoolRated ?? "",
              schoolRatedWithStatus: ratingStatus
            });

            tableDataForCSV.push({
              key: student.studentNumber,
              givenName1: student.givenName1,
              familyName: student.familyName,
              studentNumber: student.studentNumber,
              dob: moment(student.dob, DATE_FORMAT.Y_M_D).format(DATE_FORMAT.D_M_Y),
              appliedAdmission: isApplicationLodged ? FILTER_LABEL.YES : FILTER_LABEL.NO,
              srsLodged: student.srsLodged ? FILTER_LABEL.YES : FILTER_LABEL.NO,
              schoolRated: ratingStatus
            });
          });

          // Apply default sorting
          const sortedAdmin = applySort(defaultSorting, tableDataForAdmin);
          const sortedAdminForCsv = applySort(defaultSorting, tableDataForCSV);
          const sortedRater = applySort(defaultSorting, tableDataForRater);

          // Paginate data
          const newPaginatedDataForAdmin = createPaginatedTableData(1, sortedAdmin);
          const newPaginatedDataForRater = createPaginatedTableData(1, sortedRater);

          return {
            ...state,
            isLoading: false,
            data: students,
            [TABLE_TYPE.STUDENTS]: {
              ...state[TABLE_TYPE.STUDENTS],
              loading: false,
              origin: sortedAdmin,
              tableData: sortedAdmin,
              paginatedTableData: newPaginatedDataForAdmin,
              tableDataForCSV: sortedAdminForCsv
            },
            [TABLE_TYPE.SRS]: {
              ...state[TABLE_TYPE.SRS],
              loading: false,
              origin: sortedRater,
              tableData: sortedRater,
              paginatedTableData: newPaginatedDataForRater
            },
            statistics: {
              ...state.statistics,
              totalApplicants,
              totalAppliedWhitelabel
            }
          };
        }
      }
    );
    builder.addCase(fetchStudentTable.rejected, () => {
      return {
        ...StudentTableInit,
        isError: true
      };
    });
  }
});

export const { searchTable, sortTable, updateCurrentPage, filterTable, applyCriteria } =
  StudentTableSlice.actions;

export default StudentTableSlice.reducer;
